import * as React from 'react';
import { GenericGrid } from '../../components/GenericGrid';
import { Customer } from '../../dto/Customer';
import {
    Add24Regular
} from "@fluentui/react-icons";
import { TableCellLayout, TableColumnDefinition, ToolbarButton, createTableColumn } from '@fluentui/react-components';
import { useNavigate, useParams } from 'react-router-dom';
import { DateText } from '../../components/DateText';
import { TruncatedText } from '../../components/TruncatedText';
import { ensureSuccess, handleError } from '../../util/ErrorHandling';
import { useGlobalDispatch } from '../../state/GlobalStateProvider';
import { RetrieveMultipleResponse } from '../../dto/RetrieveMultipleResponse';

const rowSelector = (item: Customer) => item.guid;

export const Customers: React.FC<any> = () => {
    const { publisherId } = useParams();
    const navigate = useNavigate();
    const globalDispatch = useGlobalDispatch();

    const [customers, setCustomers] = React.useState<Customer[] | null>(null);

    React.useEffect(() => {
        if (publisherId) {
            fetch(`/api/publishers/${publisherId}/customers`, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(r => ensureSuccess(r))
                .then(r => r.json() as Promise<RetrieveMultipleResponse<Customer>>)
                .then(r => r.records)
                .then(setCustomers)
                .catch(e => handleError(e, globalDispatch))
        }
    }, [publisherId, globalDispatch]);

    const columns: TableColumnDefinition<Customer>[] = React.useMemo(() => [
        createTableColumn<Customer>({
            columnId: "Name",
            compare: (a, b) => {
                return a.name.localeCompare(b.name);
            },
            renderHeaderCell: () => {
                return "Name";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <TruncatedText text={item.name} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<Customer>({
            columnId: "Modified On",
            compare: (a, b) => {
                return a.modifiedOn.localeCompare(b.modifiedOn);
            },
            renderHeaderCell: () => {
                return "Modified On";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <DateText dateString={item.modifiedOn} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<Customer>({
            columnId: "Modified By",
            compare: (a, b) => {
                return a.modifiedBy?.guid?.localeCompare(b.modifiedBy?.guid);
            },
            renderHeaderCell: () => {
                return "Modified By";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <TruncatedText text={item.modifiedBy?.name} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<Customer>({
            columnId: "Created On",
            compare: (a, b) => {
                return a.createdOn.localeCompare(b.createdOn);
            },
            renderHeaderCell: () => {
                return "Created On";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <DateText dateString={item.createdOn} />
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<Customer>({
            columnId: "Created By",
            compare: (a, b) => {
                return a.createdBy?.guid?.localeCompare(b.createdBy?.guid);
            },
            renderHeaderCell: () => {
                return "Created By";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                        <TruncatedText text={item.createdBy?.name} />
                    </TableCellLayout>
                );
            },
        })
    ], []);

    const buttons = <>
        <ToolbarButton
            aria-label="Add new record"
            appearance="primary"
            icon={<Add24Regular />}
            onClick={() => navigate(`/workspace/publishers/${publisherId}/customers/new`)}
        />
    </>

    return (
        <div style={{ borderRadius: "5px", width: "100%", height: "100%", padding: "10px" }}>
            <GenericGrid onRowClick={(rowId) => navigate(`/workspace/publishers/${publisherId}/customers/${rowId}`)} columns={columns} items={customers} rowSelector={rowSelector} actionButtons={buttons} />
        </div>
    );
};